import React, { memo, useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useHistory } from 'react-router-dom';
import Swiper from 'react-id-swiper';
import 'swiper/css/swiper.css';
import QRCode from 'qrcode.react';
import styles from './WpOrder.module.less';

import { CONTEXT } from '../../config';
import config from '../../pages/Wallpaper/config';
import { logging } from '../../actions/log';
import { setAntiErr } from '../../actions/buttonpay';
import { updateShowWpOrder } from '../../actions/wallpaper';
import { isNeedUpdateVip, getVipState } from '../../actions/wallpaperPay';
import { addClass, removeClass, imgLocalUrl } from '../../utils/utils';
import loading from '../../static/loading.gif';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectWallpaper = createSelector(
  state => state.wallpaper,
  wallpaper => wallpaper,
);
const selectUserInfo = createSelector(
  state => state.user,
  user => user.userInfo,
);

const selectWallpaperPay = createSelector(
  state => state.wallpaperPay,
  wallpaperPay => wallpaperPay,
);

let WpPayTimeoutIndex = 0;
export default memo(() => {
  const history = useHistory();
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const logInfoData = useSelector(selectLogInfo);
  const { wpShowOrder } = useSelector(selectWallpaper);
  const { vipState } = useSelector(selectWallpaperPay);

  const [orderSkuList, setOrderSkuList] = useState(null);
  const [orderShowTag, setOrderShowTag] = useState(1);
  const [orderInfo, setOrderInfo] = useState(null);
  const [activeSku, setActiveSku] = useState(null);
  const [payResult, setPayResult] = useState({});
  const [payWayList, setPayWayList] = useState([]);

  const swiperEl = useRef(null);
  const swiperNext = useRef(null);
  const swiperPrev = useRef(null);
  const swiperParams = {
    speed: 600,
    slidesPerView: 'auto',
    centeredSlides: true,
    centeredSlidesBounds: true,
    spaceBetween: 0,
    allowTouchMove: false,
  };

  useEffect(() => {
    // 获取当前 vip 状态
    dispatch(getVipState());
  }, []);

  useEffect(() => {
    dispatch(
      logging({
        category: 'Wallpaper',
        action: 'P_Wallpaper_Buy',
        label: '',
        param2: {
          source: logInfoData?.source,
          source_p: logInfoData?.source_p,
          source_pp: logInfoData?.source_pp,
          scenetype: wpShowOrder?.scenetype || 'other',
          wptype: wpShowOrder?.wpType, // 1:壁纸  2:壁纸包  3:其他
          wallpaperId: wpShowOrder?.wallpaperId || '',
          rankId: wpShowOrder?.code || '',
        },
      }),
    );
  }, []);

  // 首先获取所有的sku
  useEffect(async () => {
    if (!orderSkuList && wpShowOrder && wpShowOrder.show === true) {
      try {
        let res = await config.getOrderSku({
          wallpaperId: wpShowOrder?.wpType === 1 ? wpShowOrder?.wallpaperId || '' : '',
          rankCode: wpShowOrder?.wpType === 2 ? wpShowOrder?.code || '' : '',
        });
        if (res?.status === 0 && res?.data) {
          if (res?.data?.vipSkuList || res?.data?.wallpaperSku || res?.data?.wallpaperRankSkuList) {
            setOrderSkuList(res?.data);
          }
        }
      } catch {}
    }
  }, [wpShowOrder, orderSkuList]);

  // 获取订阅的sku，获取成功之后就主动点击tag1 并且下单第一个sku
  useEffect(async () => {
    if (wpShowOrder.show === true && orderSkuList) {
      handleTag(1, true);
    }
  }, [wpShowOrder, orderSkuList]);

  // 定时查询是否支付成功
  useEffect(() => {
    clearTimeout(WpPayTimeoutIndex);
    WpPayTimeoutIndex = setTimeout(() => {
      if (
        orderInfo?.queryUrl &&
        payResult?.queryUrl === orderInfo?.queryUrl &&
        payResult?.status === 0
      ) {
        clearTimeout(WpPayTimeoutIndex);
      } else {
        orderPayResult();
      }
    }, 2000);
  }, [wpShowOrder, orderInfo, payResult]);

  useEffect(() => {
    if (activeSku?.supportedPayTypes) {
      let index = activeSku?.supportedPayTypes?.findIndex(e => e === 'WX');
      if (index > -1 && index !== 0) {
        let alist = activeSku?.supportedPayTypes.reverse();
        setPayWayList(alist);
      } else {
        setPayWayList(activeSku?.supportedPayTypes);
      }
    }
  }, [activeSku]);

  const orderPayResult = useCallback(async () => {
    try {
      let res = await config.orderPayResult({
        queryUrl: orderInfo?.queryUrl,
      });
      if (res && res?.status === 0) {
        loggingFunc('Wallpaper', 'R_Wallpaper_BuyResult');
        dispatch(isNeedUpdateVip(1));
        // 购买成功，调用回调函数
        if (
          wpShowOrder?.callbackTurnOrder &&
          typeof wpShowOrder?.callbackTurnOrder === 'function'
        ) {
          wpShowOrder.callbackTurnOrder(true);
        }
      }
      setPayResult({ ...res, queryUrl: orderInfo?.queryUrl });
    } catch {}
  }, [wpShowOrder, orderInfo]);

  const handleTag = useCallback(
    async tag => {
      setOrderShowTag(tag); // tag: 1,2
      if (tag === 1) {
        if (orderSkuList?.vipSkuList?.length > 0) {
          let sku = orderSkuList?.vipSkuList[0];
          handleSkuItem(sku, 3);
        }
      } else if (tag === 2) {
        if (orderSkuList?.wallpaperSku) {
          let sku = orderSkuList?.wallpaperSku;
          handleSkuItem(sku, 1);
        } else if (orderSkuList?.wallpaperRankSkuList?.length > 0) {
          let sku = orderSkuList?.wallpaperRankSkuList[0];
          handleSkuItem(sku, 2);
        }
      }
    },
    [wpShowOrder, orderSkuList],
  );
  const handleSkuItem = useCallback(
    async (sku, type) => {
      if (sku && type) {
        setActiveSku(sku);
        handleCreateOrder(sku, type);
      }
    },
    [wpShowOrder, orderSkuList],
  );

  const handleCreateOrder = useCallback(
    async (sku, type = 3) => {
      try {
        setOrderInfo(null);
        let param = {
          appType: '12',
          goodsType: 'wallpaper',
          skuId: sku?.skuId,
          type: sku?.type,
          sourceType: wpShowOrder?.wpType,
          wpType: wpShowOrder?.wpType,
          sourceKey: wpShowOrder?.wallpaperId || wpShowOrder?.code || '',
          payScene: wpShowOrder?.scenetype,
        };
        let res = await config.createOrder({ ...param });
        if (res?.status === 0 && res?.data) {
          setOrderInfo(res?.data);
        } else {
          setOrderInfo(null);
        }
      } catch {}
    },
    [wpShowOrder, orderSkuList],
  );

  const loggingFunc = useCallback(
    (category, action, param2 = {}) => {
      let skuNum = 0;
      if (orderSkuList?.vipSkuList?.length) {
        skuNum += orderSkuList?.vipSkuList?.length;
      }
      if (orderSkuList?.wallpaperSku) {
        skuNum += 1;
      }
      if (orderSkuList?.wallpaperRankSkuList?.length) {
        skuNum += orderSkuList?.wallpaperRankSkuList?.length;
      }
      dispatch(
        logging({
          category: category || 'Wallpaper',
          action: action || 'C_Wallpaper_Buy',
          label: '',
          param2: {
            source: logInfoData?.source,
            source_p: logInfoData?.source_p,
            source_pp: logInfoData?.source_pp,
            scenetype: wpShowOrder?.scenetype || 'other',
            wptype: wpShowOrder?.wpType, // 1:壁纸  2:壁纸包  3:其他
            wallpaperId: wpShowOrder?.wallpaperId || '',
            rankId: wpShowOrder?.code || '',
            sku: skuNum,
            skuVipLength: orderSkuList?.vipSkuList?.length || 0,
            skuid: activeSku?.skuId,
            skutype:
              activeSku?.type === 1 ? 'single' : activeSku?.type === 2 ? 'batch' : 'subscribe',
            price: formatPrice(activeSku?.nowPrice || activeSku?.originalPrice),
            ...param2,
          },
        }),
      );
    },
    [logInfoData, wpShowOrder, activeSku],
  );

  const handleDialogClose = useCallback(flag => {
    dispatch(
      updateShowWpOrder({
        show: false,
        wallpaperId: '',
        code: '',
        wpType: 1, // 1: 单张壁纸，2：壁纸包   3: 其他
      }),
    );
  }, []);

  const handleShowNextPrev = useCallback(
    flag => {
      if (swiperEl && swiperEl.current && swiperEl.current.swiper) {
        const swiperObj = swiperEl.current.swiper;
        if (flag === 'next') {
          swiperObj.slideNext();
          swiperObj.slideNext();
        }
        if (flag === 'prev') {
          swiperObj.slidePrev();
          swiperObj.slidePrev();
        }
        if (swiperPrev && swiperPrev.current && swiperNext && swiperNext.current) {
          swiperObj.isEnd
            ? addClass(swiperNext.current, 'order_swiper_hidden')
            : removeClass(swiperNext.current, 'order_swiper_hidden');
          swiperObj.isBeginning
            ? addClass(swiperPrev.current, 'order_swiper_hidden')
            : removeClass(swiperPrev.current, 'order_swiper_hidden');
        }
      }
    },
    [logInfoData, swiperEl, swiperNext, swiperPrev],
  );

  const handleShowRankPage = useCallback(
    sku => {
      history.push(`${CONTEXT}/wprank?code=${sku?.skuId}`);
      setTimeout(() => {
        dispatch(
          updateShowWpOrder({
            show: false,
            wallpaperId: '',
            code: '',
            wpType: 1, // 1: 单张壁纸，2：壁纸包
          }),
        );
      }, 50);
    },
    [history],
  );

  const handleShowOrderList = useCallback(() => {
    history.push(`${CONTEXT}/wallpaper/wpmine/paylist`);
    setTimeout(() => {
      dispatch(
        updateShowWpOrder({
          show: false,
          wallpaperId: '',
          code: '',
          wpType: 1, // 1: 单张壁纸，2：壁纸包
        }),
      );
    }, 50);
  }, [history]);

  const handleLink = useCallback(flag => {
    if (flag === 1) {
      window.open('https://s1.lenovomm.cn/pcstore/html/wallpaper.html');
    }
    if (flag === 2) {
      window.open('https://s1.lenovomm.cn/pcstore/html/wallpaperVip.html');
    }
  }, []);

  const formatPrice = useCallback((num1_ = '0', num2_ = '0', show0) => {
    let num1 = parseFloat(parseInt(num1_) / 100) || 0;
    let num2 = parseFloat(parseInt(num2_) / 100) || 0;
    if (num1 && !num2) {
      return show0 ? `${parseFloat(num1)}` : num1.toFixed(2);
    } else if (!num1 && num2) {
      return show0 ? `${parseFloat(num2)}` : num2.toFixed(2);
    } else if (num1 && num2) {
      return show0 ? `${parseFloat(num1 - num2)}` : (num1 - num2).toFixed(2);
    }
    return num1.toFixed(2);
  }, []);

  if (
    orderInfo?.queryUrl &&
    payResult?.queryUrl === orderInfo?.queryUrl &&
    payResult?.status === 0
  ) {
    return (
      <div className={styles.dialog_box}>
        <div className={styles.dialog_order_success}>
          <div className="order_header">
            <div className="order_header_text">{`购买壁纸订阅权益`}</div>
            <div
              className="order_header_close iconfont icon-opt_close"
              onClick={handleDialogClose}
            ></div>
          </div>
          <div className={'order_other'}>
            <div className={'other_logo_success'}></div>
            <div className={'other_info'}>支付成功</div>
            <div className={'other_text'}>
              支付订单：
              {activeSku?.type === '1'
                ? activeSku?.type === '2'
                  ? '单张壁纸购买服务'
                  : '壁纸包购买服务'
                : '壁纸订阅服务'}
            </div>
            <div className={'other_text'}>您也可以在订单中心查看订单状态</div>
            <div
              className={'btn_sure'}
              onClick={() => {
                handleShowOrderList();
              }}
            >
              查看订单
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dialog_box}>
      <div className={styles.dialog_cont}>
        <div
          className="dialog_header_close iconfont icon-opt_close"
          onClick={() => {
            handleDialogClose();
          }}
        ></div>
        <div className="dialog_header">
          {userInfo?.headimgurl ? (
            <img
              className="dialog_header_logo"
              src={
                userInfo?.headimgurl?.indexOf('http') > -1
                  ? userInfo.headimgurl
                  : imgLocalUrl(userInfo.headimgurl)
              }
            />
          ) : (
            <div className="dialog_header_logo_err"></div>
          )}
          <div className="dialog_header_cont">
            <div className="dialog_header_text">
              Hi～ <span className="dialog_header_userName">{userInfo?.lidNickName}</span>，
              {vipState.vipState === 0 && <span>您还未开通壁纸订阅服务</span>}
              {vipState.vipState === 1 && <span>您的壁纸订阅服务即将到期</span>}
              {vipState.vipState === 2 && <span>您的壁纸订阅服务即将到期</span>}
            </div>
            <div className="dialog_header_desc">
              {(() => {
                if (wpShowOrder?.scenetype === 'set') {
                  return '应版权方要求，该壁纸需购买后使用，成为订阅用户后全库壁纸免费设置。';
                }
                if (wpShowOrder?.scenetype === 'dl') {
                  return '应版权方要求，该壁纸需购买后下载，成为订阅用户后全库壁纸免费下载。';
                }
                if (
                  wpShowOrder?.scenetype === 'loop_start' ||
                  wpShowOrder?.scenetype === 'loop_single_limit5'
                ) {
                  return '您可以添加5张壁纸到轮播列表，成为订阅用户可无限添加，畅享全库壁纸免费使用。';
                }
                return '开通壁纸订阅，全库壁纸免费设为桌面及下载，还可以尊享壁纸无限轮播功能。';
              })()}
            </div>
          </div>
        </div>
        <div className="dialog_center">
          <div className="dialog_tab">
            <div
              className={orderShowTag === 1 ? 'dialog_tab_active' : 'dialog_tab_disable_l'}
              onClick={() => {
                handleTag(1);
              }}
            >
              订阅模式
            </div>
            <div className={'dialog_tab_disable_null'}></div>
            {/* {orderSkuList?.wallpaperSku || orderSkuList?.wallpaperRankSkuList?.length > 0 ? (
              <div
                className={orderShowTag === 2 ? 'dialog_tab_active' : 'dialog_tab_disable_r'}
                onClick={() => {
                  handleTag(2);
                }}
              >
                {wpShowOrder?.code ? '壁纸包购买' : '单张壁纸购买'}
              </div>
            ) : (
              <div className={'dialog_tab_disable_null'}></div>
            )} */}
          </div>
          {orderSkuList?.vipSkuList?.length > 0 ||
          orderSkuList?.wallpaperSku ||
          orderSkuList?.wallpaperRankSkuList?.length > 0 ? (
            <>
              <div className="dialog_order_list">
                {orderShowTag === 1 ? (
                  orderSkuList?.vipSkuList?.length > 0 && (
                    <>
                      <Swiper {...swiperParams} ref={swiperEl}>
                        {orderSkuList?.vipSkuList?.map((item, i) => (
                          <div
                            className={`${
                              item?.skuId === activeSku?.skuId
                                ? 'order_item order_item_active'
                                : 'order_item'
                            } ${i === 0 && 'order_item_first'} 
                              ${i === orderSkuList?.vipSkuList?.length - 1 && 'order_item_last'}`}
                            key={i}
                            onClick={() => {
                              handleSkuItem(item, 3);
                            }}
                          >
                            {item?.marketingDesc && (
                              <div
                                className="item_zhekou"
                                title={item?.marketingDesc?.length > 8 ? item?.marketingDesc : null}
                              >
                                {item?.marketingDesc}
                              </div>
                            )}
                            <div className="item_name">{item?.name}</div>
                            {item?.nowPrice && item?.originalPrice && (
                              <>
                                <div className="item_price_c">
                                  <span className="item_price_c_i">¥</span>
                                  <span>{formatPrice(item?.nowPrice, null, true)}</span>
                                </div>
                                <div className="item_price_o">
                                  {/* <span className="item_price_o_i">¥</span>
                                  <span>{formatPrice(item?.originalPrice, null, true)}</span> */}
                                  ¥{formatPrice(item?.originalPrice, null, true)}
                                </div>
                              </>
                            )}

                            {((!item?.nowPrice && item?.originalPrice) ||
                              (item?.nowPrice && !item?.originalPrice)) && (
                              <>
                                <div className="item_price_c">
                                  <span className="item_price_c_i">¥</span>
                                  <span>
                                    {formatPrice(item?.nowPrice || item?.originalPrice, null, true)}
                                  </span>
                                </div>
                              </>
                            )}
                            {item?.averageDesc && (
                              <div className="item_average" title={item?.averageDesc}>
                                {item.averageDesc}
                              </div>
                            )}
                          </div>
                        ))}
                      </Swiper>

                      <div
                        className={`order_swiper_prev order_swiper_hidden`}
                        ref={swiperPrev}
                        onClick={() => {
                          handleShowNextPrev('prev');
                        }}
                      ></div>
                      {orderSkuList?.vipSkuList?.length > 4 && (
                        <div
                          className={'order_swiper_next'}
                          onClick={() => {
                            handleShowNextPrev('next');
                          }}
                          ref={swiperNext}
                        >
                          <div className="order_swiper_next_icon"></div>
                        </div>
                      )}
                    </>
                  )
                ) : (
                  <>
                    {orderSkuList?.wallpaperSku && (
                      <div
                        className={`order_item order_item_one ${orderSkuList?.wallpaperSku
                          ?.skuId === activeSku?.skuId && 'order_item_active'}`}
                        onClick={() => {
                          handleSkuItem(orderSkuList?.wallpaperSku, 1);
                        }}
                      >
                        {orderSkuList?.wallpaperSku?.marketingDesc && (
                          <div
                            className="item_zhekou"
                            title={
                              orderSkuList?.wallpaperSku?.marketingDesc?.length > 8
                                ? orderSkuList?.wallpaperSku?.marketingDesc
                                : null
                            }
                          >
                            {orderSkuList?.wallpaperSku?.marketingDesc}
                          </div>
                        )}
                        <div className="item_name">单张壁纸</div>

                        <div className="item_wpbox">ID: {orderSkuList?.wallpaperSku?.skuId}</div>
                        {orderSkuList?.wallpaperSku?.nowPrice &&
                          orderSkuList?.wallpaperSku?.originalPrice && (
                            <>
                              <div className="item_price_c">
                                <span className="item_price_c_i">¥</span>
                                <span>
                                  {formatPrice(orderSkuList?.wallpaperSku?.nowPrice, null, true)}
                                </span>
                              </div>
                              <div className="item_price_o">
                                {/* <span className="item_price_o_i">¥</span>
                                <span>
                                  {formatPrice(
                                    orderSkuList?.wallpaperSku?.originalPrice,
                                    null,
                                    true,
                                  )}
                                </span> */}
                                ¥
                                {formatPrice(orderSkuList?.wallpaperSku?.originalPrice, null, true)}
                              </div>
                            </>
                          )}

                        {((!orderSkuList?.wallpaperSku?.nowPrice &&
                          orderSkuList?.wallpaperSku?.originalPrice) ||
                          (orderSkuList?.wallpaperSku?.nowPrice &&
                            !orderSkuList?.wallpaperSku?.originalPrice)) && (
                          <>
                            <div className="item_price_c">
                              <span className="item_price_c_i">¥</span>
                              <span>
                                {formatPrice(
                                  orderSkuList?.wallpaperSku?.nowPrice ||
                                    orderSkuList?.wallpaperSku?.originalPrice,
                                  null,
                                  true,
                                )}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {orderSkuList?.wallpaperRankSkuList &&
                      orderSkuList?.wallpaperRankSkuList?.length > 0 &&
                      orderSkuList?.wallpaperRankSkuList?.map((item, i) => (
                        <div
                          className={`order_item order_item_box ${item?.skuId ===
                            activeSku?.skuId && 'order_item_box_active'}`}
                          onClick={() => {
                            handleSkuItem(item, 2);
                          }}
                          key={i}
                        >
                          {item?.marketingDesc && (
                            <div
                              className="item_zhekou"
                              title={item?.marketingDesc?.length > 8 ? item?.marketingDesc : null}
                            >
                              {item?.marketingDesc}
                            </div>
                          )}
                          <div className="item_name_">{item?.name}</div>
                          <div
                            className="item_wpbox"
                            onClick={() => {
                              handleShowRankPage(item);
                            }}
                          >
                            <div className="item_wpbox_desc">含该张壁纸</div>
                            <div className="item_wpbox_show">查看</div>
                            <div className="item_wpbox_point iconfont icon-point_right"></div>
                          </div>
                          {item?.nowPrice && item?.originalPrice && (
                            <>
                              <div className="item_price_c">
                                <span className="item_price_c_i">¥</span>
                                <span>{formatPrice(item?.nowPrice, null, true)}</span>
                              </div>
                              <div className="item_price_o">
                                {/* <span className="item_price_o_i">¥</span>
                                <span>{formatPrice(item?.originalPrice, null, true)}</span> */}
                                ¥{formatPrice(item?.originalPrice, null, true)}
                              </div>
                            </>
                          )}

                          {((!item?.nowPrice && item?.originalPrice) ||
                            (item?.nowPrice && !item?.originalPrice)) && (
                            <>
                              <div className="item_price_c">
                                <span className="item_price_c_i">¥</span>
                                <span>
                                  {formatPrice(item?.nowPrice || item?.originalPrice, null, true)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                  </>
                )}
              </div>
              <div className="dialog_order_info">
                {orderInfo?.payUrl && (
                  <>
                    <div className="info_qrcode">
                      {orderInfo?.payUrl ? (
                        <QRCode
                          className={'qrcode_image'}
                          size={80}
                          level="Q"
                          value={orderInfo?.payUrl}
                        />
                      ) : (
                        <div className={'qrcode_image_loading'}></div>
                      )}
                    </div>
                    <div className="info_desc">
                      <div className="info_desc_line1">
                        {activeSku?.originalPrice && activeSku?.nowPrice && (
                          <>
                            <div className="info_desc_line1_c">
                              ¥{formatPrice(activeSku?.nowPrice || '0')}
                            </div>
                            <div className="info_desc_line1_o">
                              ¥{formatPrice(activeSku?.originalPrice || '0')}
                            </div>
                            {activeSku?.originalPrice > activeSku?.nowPrice && (
                              <div className="info_desc_line1_d">
                                已优惠¥
                                {formatPrice(
                                  activeSku?.originalPrice || '0',
                                  activeSku?.nowPrice || '0',
                                )}
                                元
                              </div>
                            )}
                          </>
                        )}

                        {((!activeSku?.originalPrice && activeSku?.nowPrice) ||
                          (activeSku?.originalPrice && !activeSku?.nowPrice)) && (
                          <div className="info_desc_line1_c">
                            ¥{formatPrice(activeSku?.nowPrice || activeSku?.originalPrice || '0')}
                          </div>
                        )}
                      </div>
                      <div className="info_desc_line2">
                        {payWayList?.map((item, i) => {
                          if (item === 'WX') {
                            return (
                              <div key={i} className="order_right">
                                <span className="order_right_wx" />
                                微信扫码支付
                              </div>
                            );
                          }
                          if (item === 'ALI') {
                            return (
                              <div key={i} className="order_right">
                                <span className="order_right_zfb" />
                                支付宝扫码支付
                              </div>
                            );
                          }
                        })}
                      </div>
                      <div className="info_desc_line3">
                        <div>该商品为虚拟商品，不接受退换</div>
                        <div>
                          购买即代表您已阅读并同意
                          <span
                            className="info_desc_line_a"
                            onClick={() => {
                              handleLink(1);
                            }}
                          >
                            《非商业用途声明》
                          </span>
                          {orderShowTag === 1 && (
                            <>
                              、
                              <span
                                className="info_desc_line_a"
                                onClick={() => {
                                  handleLink(2);
                                }}
                              >
                                《联想应用商店壁纸订阅服务协议》
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="dialog_order_loading">
              <img className="dialog_order_loading_gif" src={loading} alt="" />
              <div className="dialog_order_loading_text">加载中...</div>
            </div>
          )}
        </div>
        <div className="dialog_bottom">
          <div className="dialog_bottom_header">
            <div className="dialog_bottom_header_left"></div>
            <div className="dialog_bottom_header_title">壁纸订阅服务特权</div>
            <div className="dialog_bottom_header_right"></div>
          </div>
          <div className="dialog_powers">
            <div className="dialog_power">
              <div className="power_logo logo1"></div>
              <div className="dialog_power_info">
                <div className="dialog_power_name">全库壁纸</div>
                <div className="dialog_power_desc">任意用</div>
              </div>
            </div>
            <div className="dialog_power">
              <div className="power_logo logo2"></div>
              <div className="dialog_power_info">
                <div className="dialog_power_name">精美壁纸</div>
                <div className="dialog_power_desc">随意下</div>
              </div>
            </div>
            <div className="dialog_power">
              <div className="power_logo logo3"></div>
              <div className="dialog_power_info">
                <div className="dialog_power_name">4K壁纸</div>
                <div className="dialog_power_desc">持续上新</div>
              </div>
            </div>
            <div className="dialog_power">
              <div className="power_logo logo4"></div>
              <div className="dialog_power_info">
                <div className="dialog_power_name">壁纸轮播</div>
                <div className="dialog_power_desc">列表无限</div>
              </div>
            </div>
            <div className="dialog_power">
              <div className="power_logo logo5"></div>
              <div className="dialog_power_info">
                <div className="dialog_power_name">3端共享</div>
                <div className="dialog_power_desc">会员权益</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
